import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import Drawer from '@material-ui/core/Drawer';
import { ChevronUpIcon, ChevronDownIcon } from '@qualtrics/ui-react/icons';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { makeStyles } from '@material-ui/core';
import { Link } from '../Link';
import logo from './logo.svg';
import PageSearch from './PageSearch';
import styles from './Sidebar.module.scss';

const useStyles = makeStyles({
    menu: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    paper: {
        position: 'relative',
        zIndex: 1000,
    },
    list: {
        paddingBottom: 24,
        paddingTop: 0,
    },
    headerListItem: {
        padding: 0,
    },
    headerListItemDivider: {
        borderBottom: '1px solid #E1EDF9',
    },
});

function calculateSubMenuState(items, selectedPath) {
    const calculatedSubMenuState = {};
    items.forEach(item => {
        if (
            item.children &&
            item.children.some(child => selectedPath.includes(child.path))
        ) {
            calculatedSubMenuState[item.title] = true;
        }
    });
    return calculatedSubMenuState;
}

function Sidebar({ items = [] }) {
    let initialPath = window.location.pathname;
    if (initialPath === '/') {
        initialPath = items[0].path;
    }
    const [selectedPath, setSelectedPath] = useState(initialPath);
    const [subMenuState, setSubMenuState] = useState(
        calculateSubMenuState(items, selectedPath)
    );
    function handleSubMenuClick(title) {
        setSubMenuState(prevState => ({ [title]: !prevState[title] }));
    }

    function handleSearchSelect(page) {
        setSubMenuState(calculateSubMenuState(items, page.path));
        setSelectedPath(page.path);
    }

    const classes = useStyles();
    function getCollapsableListItems(listItems, paddingLeft) {
        return listItems
            .filter(item => {
                const { skipSidebar } = item;
                return !skipSidebar;
            })
            .map(item => {
                const { path, title, children } = item;

                if (children) {
                    return (
                        <div key={title}>
                            <ListItem
                                key={title}
                                className={styles.item}
                                button
                                classes={{ root: classes.menu }}
                                onClick={() => handleSubMenuClick(title)}
                                component="div"
                            >
                                {title}
                                {subMenuState[title] ? (
                                    <ChevronUpIcon />
                                ) : (
                                    <ChevronDownIcon />
                                )}
                            </ListItem>

                            <Collapse
                                className={styles.collapse}
                                in={subMenuState[title]}
                                timeout="auto"
                                unmountOnExit
                            >
                                {getCollapsableListItems(
                                    children,
                                    paddingLeft + 16
                                )}
                            </Collapse>
                        </div>
                    );
                }

                return (
                    <Link
                        key={path}
                        to={path}
                        onClick={() => setSelectedPath(path)}
                    >
                        <ListItem
                            className={styles.item}
                            button
                            selected={selectedPath === path}
                            component="div"
                        >
                            <div style={{ paddingLeft }}>{title}</div>
                        </ListItem>
                    </Link>
                );
            });
    }

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            open
            classes={{ paperAnchorLeft: classes.paper }}
            className={styles.drawer}
        >
            <List component="div" classes={{ padding: classes.list }}>
                <ListItem
                    divider
                    button
                    component="div"
                    classes={{
                        root: classes.headerListItem,
                        divider: classes.headerListItemDivider,
                    }}
                >
                    <Link
                        className={styles.headerLink}
                        to="/getting-started"
                        onClick={() => setSelectedPath('/getting-started')}
                    >
                        <div className={styles.logo}>
                            <img
                                className={styles.logoImage}
                                src={logo}
                                alt="XM Logo"
                            />
                            <span className={styles.title}>
                                Qualtrics{' '}
                                <strong className={styles.subTitle}>
                                    Design System
                                </strong>
                            </span>
                        </div>
                    </Link>
                </ListItem>
                <ListItem component="div">
                    <PageSearch onSelect={handleSearchSelect} />
                </ListItem>
                {getCollapsableListItems(items, 0)}
            </List>
        </Drawer>
    );
}

Sidebar.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            path: PropTypes.string,
            content: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
            children: PropTypes.any,
        })
    ),
};

Sidebar.defaultProps = {
    items: [],
};

export default Sidebar;
